import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Easing,
  Dimensions,
  Image,
  ImageBackground,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import FastImage from "react-native-fast-image";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { AppIcons } from "../../assets/images";
import { AppTheme } from "../../utils/theme";
import LinearGradient from "react-native-linear-gradient";
import Spacer from "../../components/global/Spacer";
import { interstitial, LUCKY_SPIN, goBackOrHome } from "../../utils/contants";
import { setAppOpenAdPaused } from "../../utils/useOpenAppAd";
import { AdEventType } from "react-native-google-mobile-ads";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { ROUTE_NAME } from "../../utils/routes";
import { useAppSelector } from "../../redux/storeHook";
import AnimatedLottieModal from "../../components/global/AnimatedLottieModal";
import api from "../../utils/axiosinstance";
import axios from "axios";
import Sound from "react-native-sound";

const { width } = Dimensions.get("window");

const WHEEL_SIZE = width * 0.8;
const RADIUS = WHEEL_SIZE / 2;
const LABEL_RADIUS = RADIUS - 180; // Adjusted for better positioning
const IMAGE_OFFSET = 22.5; // Half segment for 8 segments
const FREE_SPIN_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_AD_SPINS_PER_DAY = 10;

/* 🎁 Rewards - 8 segments: wins alternating with loses, matching image approx */
type Reward = {
  label: string;
  value: number;
};

const rewards: Reward[] = [
  { label: "", value: 10 },
  { label: "100", value: 100 },
  { label: "", value: 50 },
  { label: "200", value: 200 },
  { label: "", value: 20 },
  { label: "500", value: 500 },
  { label: "", value: 0 },
  { label: "1000", value: 1000 },
];

const SEGMENT_ANGLE = 360 / rewards.length;

const LuckySpinTablet: React.FC = () => {
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const [isSpinning, setIsSpinning] = useState(false);
  const [coins, setCoins] = useState("0"); // Changed to string for better handling/formatting
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [remainingTime, setRemainingTime] = useState(0); // Remaining seconds for next free spin
  const [pendingAd, setPendingAd] = useState(false);
  const [animationModal, setAnimationModal] = useState(false);
  const { userData } = useAppSelector((s) => s.user);
  const { isSoundOn } = useAppSelector((state) => state.user);

  const navigation = useNavigation<NativeStackNavigationProp<any>>();

  // Sound reference
  const spinSound = useRef<Sound | null>(null);

  /* Preload the spin sound on component mount */
  useEffect(() => {
    // Enable playback in silence mode (iOS)
    Sound.setCategory("Playback");

    // Preload the sound file (assume spin.mp3 is in your assets or bundle)
    spinSound.current = new Sound("spin.mp3", Sound.MAIN_BUNDLE, (error) => {
      if (error) {
        console.log("Failed to load spin sound", error);
      } else {
        console.log("Spin sound preloaded successfully");
      }
    });

    // Cleanup sound on unmount
    return () => {
      if (spinSound.current) {
        spinSound.current.release();
      }
    };
  }, []);

  /* Get local date string YYYY-MM-DD */
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /* Load last free spin time and initialize timer on mount */
  useEffect(() => {
    const loadLastFreeSpin = async () => {
      try {
        const lastFreeTimeStr = await AsyncStorage.getItem("lastFreeSpin");
        if (lastFreeTimeStr) {
          const lastFreeTime = parseInt(lastFreeTimeStr, 10);
          const now = Date.now();
          const elapsed = now - lastFreeTime;
          const remainingMs = Math.max(0, FREE_SPIN_COOLDOWN_MS - elapsed);
          setRemainingTime(Math.floor(remainingMs / 1000));
        }
      } catch (error) {
        console.error("Error loading last free spin time:", error);
      }
    };
    loadLastFreeSpin();
  }, []);

  /* Timer effect: Update remaining time every second */
  useEffect(() => {
    //@ts-ignore
    let interval: NodeJS.Timeout | null = null;
    if (remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [remainingTime]);

  /* Format remaining time as HH:MM:SS */
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const unsubscribeLoaded = interstitial.addAdEventListener(
      AdEventType.LOADED,
      () => {
        console.log("@ interstitial ad loaded");
        if (pendingAd) {
          setPendingAd(false);
          setAppOpenAdPaused(true);
          interstitial.show();
        }
      }
    );
    const unsubscribeOpened = interstitial.addAdEventListener(
      AdEventType.OPENED,
      () => {
        console.log("@ interstitial ad opened");
      }
    );
    const unsubscribeClosed = interstitial.addAdEventListener(
      AdEventType.CLOSED,
      async () => {
        console.log("@ interstitial ad closed");
        setAppOpenAdPaused(false, 1000);
        // Increment ad spin count for today
        try {
          const today = getTodayDate();
          const storedCountStr = await AsyncStorage.getItem(
            `ad_spins_${today}`
          );
          const count = storedCountStr ? parseInt(storedCountStr, 10) : 0;
          const newCount = count + 1;
          await AsyncStorage.setItem(`ad_spins_${today}`, newCount.toString());
        } catch (error) {
          console.error("Error incrementing ad spins:", error);
        }
        doSpin(); // Directly spin after ad closes
        // Reload ad for next time
        interstitial.load();
      }
    );
    interstitial.load();
    return () => {
      unsubscribeLoaded();
      unsubscribeOpened();
      unsubscribeClosed();
    };
  }, []); // Removed [pendingAd] dependency to prevent re-adding listeners on every pendingAd change

  /* 🎡 Core spin logic (used for both free and paid spins) */
  const doSpin = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setSelectedIndex(null);

    // Play spin sound when starting the spin only if sound is on
    if (isSoundOn && spinSound.current) {
      spinSound.current.play((success) => {
        if (!success) {
          console.log("Playback failed due to audio decoding errors");
        }
      });
    }

    const randomIndex = Math.floor(Math.random() * rewards.length);
    const selectedReward = rewards[randomIndex];

    const segmentCenter =
      randomIndex * SEGMENT_ANGLE + SEGMENT_ANGLE / 2 + IMAGE_OFFSET;

    const targetAngle = (360 - segmentCenter + 360) % 360;
    const spinTo = 360 * 5 + targetAngle;

    Animated.timing(rotateAnim, {
      toValue: spinTo,
      duration: 4500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      // Stop spin sound when animation ends only if sound is on
      if (isSoundOn && spinSound.current) {
        spinSound.current.stop(() => {
          console.log("Spin sound stopped");
        });
      }

      rotateAnim.setValue(spinTo % 360);
      setSelectedIndex(randomIndex);
      setIsSpinning(false);

      if (selectedReward.value > 0) {
        // Winner: set coins and call API, show modal on success
        setCoins(selectedReward.value.toString());
        coinApiRequest(selectedReward.value);
      } else {
        setAnimationModal(true);
        setCoins(selectedReward.value.toString());
        // Loser: show alert
        // Alert.alert(
        //   "Better Luck Next Time!",
        //   "Spin again for your chance to win coins!"
        // );
      }
    });
  };

  /* Handle watching ad for spin */
  const handleWatchAd = () => {
    console.log("Watch Advertisement pressed");
    if (interstitial.loaded) {
      // If already loaded, show immediately (no need for pending flag)
      setAppOpenAdPaused(true);
      interstitial.show();
    } else {
      // If not loaded, set pending and load
      setPendingAd(true);
      interstitial.load();
    }
  };

  /* 🎡 Main spin entry point with checks */
  const spinWheel = async () => {
    if (!userData?.access_token) {
      Alert.alert("Login", "To purchase coins, you need to log in first.", [
        {
          text: "Login",
          onPress: () => {
            navigation.navigate(ROUTE_NAME.LoginScreen);
          },
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]);
      return;
    }

    setAnimationModal(false);
    if (isSpinning) return;

    try {
      const lastFreeTimeStr = await AsyncStorage.getItem("lastFreeSpin");
      const now = Date.now();
      const lastFreeTime = lastFreeTimeStr ? parseInt(lastFreeTimeStr, 10) : 0;
      const canFreeSpin = now - lastFreeTime > FREE_SPIN_COOLDOWN_MS;

      if (canFreeSpin) {
        // Free spin
        doSpin();
        // Update last free spin time and reset timer
        await AsyncStorage.setItem("lastFreeSpin", now.toString());
        setRemainingTime(24 * 60 * 60); // Reset to 24 hours in seconds
      } else {
        // Check ad spin limit
        const today = getTodayDate();
        const storedCountStr = await AsyncStorage.getItem(`ad_spins_${today}`);
        const count = storedCountStr ? parseInt(storedCountStr, 10) : 0;

        if (count >= MAX_AD_SPINS_PER_DAY) {
          Alert.alert(
            "Daily Limit Reached",
            `You have reached today’s limit of ${MAX_AD_SPINS_PER_DAY} ad spins.Please return tomorrow to continue, or purchase coins to proceed now.`,
            [
              { text: "OK", style: "default" },
              {
                text: "Purchase Coins",
                onPress: () => navigation.navigate(ROUTE_NAME.InPurchaseScreen),
              },
            ]
          );
          return;
        }

        // Show alert for ad spin
        Alert.alert(
          "Spin Locked",
          "Watch an Ad to unlock a spin!",
          [
            {
              text: "Watch Ad",
              onPress: handleWatchAd, // Use the new handler
            },
            { text: "Cancel", style: "cancel" },
          ],
          { cancelable: true }
        );
      }
    } catch (error) {
      console.error("Error in spinWheel:", error);
      // Fallback or handle error
    }
  };

  const rotateInterpolation = rotateAnim.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  /* 🟢 Green highlight overlay */
  const renderHighlight = () => {
    if (selectedIndex === null) return null;

    const angle = selectedIndex * SEGMENT_ANGLE + IMAGE_OFFSET;

    return (
      <View
        pointerEvents="none"
        style={[
          styles.highlight,
          {
            transform: [{ rotate: `${angle}deg` }],
          },
        ]}
      />
    );
  };

  /* Handle coin updates as string for better consistency/formatting */
  const handleCoinUpdate = (coinAmount: number | string) => {
    const coinStr =
      typeof coinAmount === "number" ? coinAmount.toString() : coinAmount;
    setCoins(coinStr);
  };

  const coinApiRequest = async (coin: number) => {
    try {
      const body = {
        coinsToAdd: coin, // Ensure number for API
      };
      console.log(" coin body : ", body);
      const res = await api.post(LUCKY_SPIN, body);
      if (res.data?.status) {
        console.log(" lucky spin api response : ", res.data.status);
        // Use handler for string update from API response
        handleCoinUpdate(res.data.coinsAdded);
        setAnimationModal(true);
      } else {
        // If API fails or no status, alert error (optional, or just log)
        Alert.alert("Oops!", "Failed to add coins. Please try again.");
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        console.log("Response error:", err.response.data);
      } else {
        console.log("Unexpected error:", err);
      }
      console.log("@ err in completeLesson : ", err);
      // Alert on error
      Alert.alert("Oops!", "Failed to add coins. Please try again.");
    }
  };

  return (
    <FastImage source={AppIcons.SpinBackground1} style={styles.container}>
      <TouchableOpacity
        onPress={() => goBackOrHome(navigation)}
        style={styles.backIcon}
      >
        <Image
          source={AppIcons.BackIcon}
          style={{ width: 70, height: 55 }}
          resizeMode={"contain"}
        />
      </TouchableOpacity>

      <SafeAreaView style={styles.container}>
        {/* 🔙 Back */}

        {/* 🎉 Title Image */}
        <View style={{ alignItems: "center", marginBottom: "20%" }}>
          <FastImage
            source={AppIcons.Luck}
            style={styles.headerImage}
            resizeMode="contain"
          />
        </View>
        <FastImage
          source={AppIcons.SpinBackground}
          style={styles.wheelBackimg}
          resizeMode="contain"
        ></FastImage>
        {/* 🎡 Wheel */}
        <Animated.View
          style={[
            styles.wheelContainer,
            { transform: [{ rotate: rotateInterpolation }] },
          ]}
        >
          <FastImage
            source={AppIcons.Spin}
            style={styles.wheelImage}
            resizeMode="contain"
          />

          {renderHighlight()}

          {/* 🔢 Labels - Render for all segments: coins with icon on dark blue, 0 on light */}
          {rewards.map((reward, index) => {
            const theta =
              index * SEGMENT_ANGLE + SEGMENT_ANGLE / 2 + IMAGE_OFFSET;
            const rad = (theta * Math.PI) / 180;

            const x = LABEL_RADIUS * Math.sin(rad);
            const y = -LABEL_RADIUS * Math.cos(rad);

            const hasIcon = reward.value > 0;
            const labelOffset = hasIcon ? -30 : -15; // Adjust offset based on icon presence

            return (
              <View
                key={index}
                style={{
                  position: "absolute",
                  left: RADIUS + x + labelOffset,
                  top: RADIUS + y - 12,
                  transform: [{ rotate: `${theta}deg` }],
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Text style={styles.labelText}>{reward.label}</Text>
              </View>
            );
          })}
        </Animated.View>
        <View style={{ position: "absolute", top: "24%" }}>
          <Image source={AppIcons.Point} style={{ height: 170, width: 150 }} />
        </View>
        {/* ⏰ Timer Display */}
        {remainingTime > 0 && (
          <View style={styles.timerContainer}>
            <Text style={styles.timerText}>
              Next Free Spin In: {formatTime(remainingTime)}
            </Text>
          </View>
        )}
        <TouchableOpacity
          activeOpacity={1}
          style={{ alignSelf: "center", marginTop: "15.5%" }}
          onPress={spinWheel}
          disabled={isSpinning}
        >
          <LinearGradient
            colors={[
              AppTheme.colors.linearGradient_5,
              AppTheme.colors.linearGradient_6,
            ]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.googleBtn1}
          >
            <LinearGradient
              colors={[
                AppTheme.colors.linearGradient_1,
                AppTheme.colors.linearGradient_2,
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.googleBtn}
            >
              <Text style={styles.startText}>SPIN</Text>
            </LinearGradient>
          </LinearGradient>
        </TouchableOpacity>
      </SafeAreaView>
      {animationModal && (
        <View style={styles.modalContainer}>
          <AnimatedLottieModal coins={coins} />
          {/* Pass as string; adjust modal if needed to handle string */}
        </View>
      )}
    </FastImage>
  );
};

export default LuckySpinTablet;

/* ================== STYLES ================== */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  backIcon: {
    position: "absolute",
    top: 70,
    right: 0,
    zIndex: 10,
  },
  headerImage: {
    width: 400,
    height: 200,
    marginBottom: 10,
  },
  wheelContainer: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    justifyContent: "center",
    alignItems: "center",
  },
  wheelImage: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    borderRadius: RADIUS,
    // zIndex: 99,
  },
  wheelBackimg: {
    width: WHEEL_SIZE + 200,
    height: WHEEL_SIZE + 200,
    position: "absolute",
    borderRadius: RADIUS,
    zIndex: -1,
    marginBottom: -90,
  },
  labelText: {
    color: "#fff",
    fontSize: 16,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD,
  },
  highlight: {
    position: "absolute",
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    borderRadius: RADIUS,
  },
  spinButton: {
    marginTop: 40,
    paddingHorizontal: 60,
    paddingVertical: 15,
    borderRadius: 30,
  },
  spinText: {
    fontSize: 20,
    fontWeight: "bold",
  },
  coinsText: {
    marginTop: 20,
    fontSize: 18,
    fontWeight: "600",
  },
  googleBtn: {
    width: 300,
    height: 68,
    borderRadius: 40,
    justifyContent: "center",
  },

  googleBtn1: {
    width: 300,
    height: 75,
    borderRadius: 45,
  },
  startText: {
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    fontSize: 35,
    color: AppTheme.colors.white,
    textAlign: "center",
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
  },
  /* Timer Styles */
  timerContainer: {
    position: "absolute",
    top: "87%", // Position above the spin button, adjust as needed
    alignItems: "center",
    zIndex: 5,
  },
  timerText: {
    color: AppTheme.colors.white,
    fontSize: 18,
    fontFamily: AppTheme.fonts.ALEXANDRIA_BOLD, // Use existing bold font
    textAlign: "center",
  },
  modalContainer: {
    flex: 1,
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
  },
});
